/**
 * Feature requests — shared board with voting.
 *
 * A MODAL RATHER THAN A PAGE, copied from Conduit CRM for the reason its author
 * gave: the moment someone thinks "this should do X" is while they are in the
 * middle of doing something else. Navigating away to a form means losing their
 * place, and most people simply will not bother.
 *
 * Two lists, in this order and not the other way round:
 *
 *  1. What has already been asked for, so somebody about to type "let me export
 *     to CSV" sees it exists and votes instead. A vote is a better signal than a
 *     fifth duplicate, and it costs them one click instead of a paragraph.
 *  2. The form, underneath.
 *
 * WHAT OTHER TENANTS SEE. The board is shared across every organisation, so
 * anything on it is public to all of them. The server only ever returns the
 * title, the details and the vote count — never who asked or which org they are
 * from (see functions/src/features.ts). The "Yours" tag is computed for your own
 * org only; nobody else sees it on your rows.
 *
 * Context (app version, the view they were on) is captured automatically. A
 * request read three months later is only actionable if you know what the person
 * was looking at, and nobody types that in voluntarily.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

type Status = 'new' | 'planned' | 'in-progress' | 'shipped' | 'declined';

interface BoardItem {
  id: string;
  title: string;
  details: string;
  status: Status;
  voteCount: number;
  createdAt: number;
  voted: boolean;
  mine: boolean;
}

interface MyItem {
  id: string;
  title: string;
  status: Status;
  voteCount: number;
  createdAt: number;
}

/**
 * Deliberately plain-language, matching Conduit. "new" is an internal word; the
 * person who sent it wants to know it arrived.
 */
const STATUS_LABEL: Record<Status, string> = {
  new: 'Received',
  planned: 'Planned',
  'in-progress': 'Being built',
  shipped: 'Shipped',
  declined: 'Not planned',
};

const STATUS_VAR: Record<Status, string> = {
  new: 'var(--text-muted)',
  planned: 'var(--blue)',
  'in-progress': 'var(--yellow)',
  shipped: 'var(--green)',
  declined: 'var(--text-muted)',
};

const MAX_TITLE = 140;
const MAX_DETAILS = 4000;

export function FeatureRequests({
  orgId,
  view,
  version,
  onClose,
}: {
  orgId: string | null;
  /** The view they were on when they had the idea. Captured, not typed. */
  view: string;
  version: string;
  onClose: () => void;
}) {
  const [board, setBoard] = useState<BoardItem[] | null>(null);
  const [mine, setMine] = useState<MyItem[]>([]);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const [b, m] = await Promise.all([
        httpsCallable(functions, 'listFeatureRequests')({ orgId }),
        httpsCallable(functions, 'myFeatureRequests')({}),
      ]);
      setBoard(((b.data as { requests: BoardItem[] }).requests ?? []).slice());
      setMine((m.data as { requests: MyItem[] }).requests ?? []);
    } catch (err) {
      setBoard([]);
      setError(err instanceof Error ? err.message : 'Could not load the board.');
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function vote(item: BoardItem) {
    // Optimistic: a vote button that waits on a round trip feels broken, and the
    // server is the authority either way — a failure reloads the truth.
    setBoard((b) =>
      (b ?? []).map((x) =>
        x.id === item.id
          ? { ...x, voted: !x.voted, voteCount: x.voteCount + (x.voted ? -1 : 1) }
          : x
      )
    );
    try {
      await httpsCallable(functions, 'voteFeatureRequest')({ id: item.id, orgId });
    } catch {
      void load();
    }
  }

  async function submit() {
    if (!title.trim()) { setError('Give it a short title.'); return; }
    if (details.trim().length < 10) {
      setError('Add a little more detail so we know what you need.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await httpsCallable(functions, 'submitFeatureRequest')({
        orgId,
        title: title.trim(),
        details: details.trim(),
        appVersion: version,
        view,
      });
      setSent(true);
      setTitle('');
      setDetails('');
      await load();
    } catch (err) {
      // Keep what they typed. Wiping a paragraph and saying "try again" is how
      // you lose the request entirely.
      setError(err instanceof Error ? err.message : 'That could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const open = board?.filter((b) => b.status !== 'shipped') ?? [];
  const shipped = board?.filter((b) => b.status === 'shipped') ?? [];

  return (
    <div className="backdrop open" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="modal-ttl">Feature requests</div>
          <button className="modal-x" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-bd">
          {error && <div className="banner banner-error" style={{ marginTop: 0 }}>{error}</div>}
          {sent && (
            <div className="banner banner-ok">
              Sent — thank you. It is in the queue with the page you were on and your build
              attached, so we can see the context.
            </div>
          )}

          <p className="panel-note" style={{ marginTop: 0 }}>
            Everyone using the dashboard sees this board and can vote. Titles are shared;
            who asked is not.
          </p>

          {board === null ? (
            <div className="empty">Loading…</div>
          ) : (
            <>
              {open.length > 0 && (
                <div className="fr-list">
                  {open.map((r) => (
                    <Row key={r.id} item={r} onVote={() => void vote(r)} />
                  ))}
                </div>
              )}

              {shipped.length > 0 && (
                <>
                  <div className="flbl" style={{ marginTop: 16 }}>Already shipped</div>
                  <div className="fr-list">
                    {shipped.map((r) => (
                      <Row key={r.id} item={r} onVote={() => void vote(r)} />
                    ))}
                  </div>
                </>
              )}

              {open.length === 0 && shipped.length === 0 && (
                <div className="empty">
                  Nothing has been asked for yet. Be the first — the list below is what gets built.
                </div>
              )}
            </>
          )}

          <div className="fr-sep" />

          <div className="flbl">What would you like the dashboard to do?</div>
          <input
            className="finput"
            value={title}
            maxLength={MAX_TITLE}
            placeholder="Short title — e.g. Email me when a machine goes failed"
            onChange={(e) => { setTitle(e.target.value); setSent(false); }}
          />

          <div className="flbl" style={{ marginTop: 12 }}>Tell us more</div>
          <textarea
            className="finput"
            rows={5}
            value={details}
            maxLength={MAX_DETAILS}
            placeholder="What are you trying to do, and what gets in the way today? The more specific, the more likely it gets built."
            onChange={(e) => { setDetails(e.target.value); setSent(false); }}
          />

          {mine.length > 0 && (
            <>
              <div className="flbl" style={{ marginTop: 18 }}>Your previous requests</div>
              <div className="fr-mine">
                {mine.slice(0, 8).map((r) => (
                  <div className="fr-mine-row" key={r.id}>
                    <span className="fr-mine-title">{r.title}</span>
                    <span className="fr-pill" style={{ color: STATUS_VAR[r.status] }}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-ft">
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
            {busy ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ item, onVote }: { item: BoardItem; onVote: () => void }) {
  return (
    <div className="fr-row">
      <button
        className={'fr-vote' + (item.voted ? ' voted' : '')}
        onClick={onVote}
        title={item.voted ? 'Remove your vote' : 'Vote for this'}
        aria-pressed={item.voted}
      >
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 15l-6-6-6 6" />
        </svg>
        <span>{item.voteCount}</span>
      </button>
      <div className="fr-body">
        <div className="fr-title">
          {item.title}
          {item.mine && <span className="fr-yours">Yours</span>}
        </div>
        <div className="fr-details">{item.details}</div>
      </div>
      <span className="fr-pill" style={{ color: STATUS_VAR[item.status] }}>
        {STATUS_LABEL[item.status] ?? item.status}
      </span>
    </div>
  );
}
